import { memo, useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { panelCls } from "@/utils/panelCls";

/**
 * 참조 스펙트럼 (B2 · 2026-08-15).
 *
 * 중앙대 표준물질 DB 의 실측 MS 스펙트럼을 막대로 표시한다. 2026-08-14 회신으로
 * 받은 NIST MS Search 화면 구성 중 "매칭된 물질의 MS 값·그래프"에 해당한다.
 *
 * **없는 것을 있는 것처럼 그리지 않는다.** 스펙트럼이 없으면 빈 차트를 내지 않고
 * 미보유라고 말한다 — 무기염·기체류 등 GC-MS 로 측정하지 않는 물질이 실제로
 * 17종 있고, 빈 차트는 "측정했더니 아무것도 없다" 로 읽힌다.
 */

export interface ReferencePeak {
  mz: number;
  intensity: number;
}

interface ReferenceSpectrumProps {
  compoundName: string;
  peaks: ReferencePeak[];
  /** 어느 라이브러리의 스펙트럼인지. 출처 없는 스펙트럼은 근거가 되지 못한다. */
  sourceLabel?: string | null;
  height?: number;
}

const ReferenceSpectrum = ({
  compoundName,
  peaks,
  sourceLabel,
  height = 220,
}: ReferenceSpectrumProps) => {
  const { data, basePeak } = useMemo(() => {
    if (peaks.length === 0) return { data: [], basePeak: null };
    const max = Math.max(...peaks.map((p) => p.intensity));
    const base = peaks.reduce((a, b) => (b.intensity > a.intensity ? b : a));
    return {
      data: peaks.map((p) => ({
        mz: p.mz,
        // 상대강도로 표시한다. 절대 counts 는 측정 조건에 따라 달라져
        // 서로 다른 출처의 스펙트럼과 비교할 수 없다.
        relative: max > 0 ? (p.intensity / max) * 100 : 0,
        intensity: p.intensity,
      })),
      basePeak: base,
    };
  }, [peaks]);

  if (peaks.length === 0) {
    return (
      <div className={panelCls("standard")}>
        <h4 className="text-sm font-semibold text-gray-900 mb-1">
          참조 스펙트럼
        </h4>
        <p className="text-xs text-gray-500">
          참조 스펙트럼 <span className="font-medium">미보유</span> — 이 물질은
          표준물질 DB 에 측정 스펙트럼이 없습니다.
        </p>
      </div>
    );
  }

  return (
    <div className={panelCls("standard")}>
      <div className="flex items-baseline justify-between mb-2 flex-wrap gap-1">
        <h4 className="text-sm font-semibold text-gray-900">
          참조 스펙트럼
          <span className="ml-2 text-xs font-normal text-gray-500">
            {compoundName}
          </span>
        </h4>
        <span className="text-xs text-gray-500">
          피크 {peaks.length}개
          {basePeak ? ` · 기준 피크 m/z ${basePeak.mz}` : ""}
        </span>
      </div>

      <ResponsiveContainer width="100%" height={height}>
        <BarChart
          data={data}
          margin={{ top: 4, right: 8, bottom: 16, left: 0 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            dataKey="mz"
            type="number"
            domain={["dataMin - 5", "dataMax + 5"]}
            tick={{ fontSize: 11 }}
            label={{
              value: "m/z",
              position: "insideBottom",
              offset: -8,
              fontSize: 11,
            }}
          />
          <YAxis
            tick={{ fontSize: 11 }}
            domain={[0, 100]}
            label={{
              value: "상대강도 (%)",
              angle: -90,
              position: "insideLeft",
              fontSize: 11,
            }}
          />
          <Tooltip
            formatter={(value) => [
              `${Number(value ?? 0).toFixed(1)}%`,
              "상대강도",
            ]}
            labelFormatter={(mz) => `m/z ${mz}`}
          />
          {/* 2026-08-28: 이 저장소의 다른 차트 8개는 전부 애니메이션을 끈다
              (MassSpectrum·SpectrumComparison·TICPlot·MZRT 등). 여기만 켜져
              있어서, 물질을 바꿀 때마다 막대가 0 에서 자라 올라오는 것이
              "화면이 다시 그려진다"는 인상을 더했다. */}
          <Bar
            dataKey="relative"
            fill="#2563eb"
            barSize={2}
            isAnimationActive={false}
          />
        </BarChart>
      </ResponsiveContainer>

      {sourceLabel && (
        <p className="mt-1 text-[11px] text-gray-500">출처: {sourceLabel}</p>
      )}
    </div>
  );
};

export default memo(ReferenceSpectrum);
